import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { AuthUser, LoginResponse, MeResponse, Role } from '@crm/shared';
import { api, setOnUnauthorized } from '../../lib/api';
import { getAccessToken, setAccessToken } from '../../lib/token';

type AuthState =
  | { status: 'loading' }
  | { status: 'authenticated'; user: AuthUser }
  | { status: 'unauthenticated' };

type AuthContextValue = {
  state: AuthState;
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  hasRole: (...roles: Role[]) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const bootstrapped = useRef(false);

  const handleUnauthorized = useCallback(() => {
    setAccessToken(null);
    setState({ status: 'unauthenticated' });
  }, []);

  useEffect(() => {
    setOnUnauthorized(handleUnauthorized);
    return () => setOnUnauthorized(null);
  }, [handleUnauthorized]);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    const bootstrap = async () => {
      const token = getAccessToken();
      try {
        if (token) {
          const me = await api.get<MeResponse>('/auth/me');
          setState({ status: 'authenticated', user: me.data.user });
          return;
        }
        // Brak accessTokenu — spróbuj odzyskać sesję z refresh cookie.
        const refresh = await api.post<{ accessToken: string }>('/auth/refresh');
        setAccessToken(refresh.data.accessToken);
        const me = await api.get<MeResponse>('/auth/me');
        setState({ status: 'authenticated', user: me.data.user });
      } catch {
        setAccessToken(null);
        setState({ status: 'unauthenticated' });
      }
    };

    void bootstrap();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post<LoginResponse>('/auth/login', { email, password });
    setAccessToken(res.data.accessToken);
    setState({ status: 'authenticated', user: res.data.user });
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignoruj — i tak czyścimy lokalny stan
    }
    setAccessToken(null);
    setState({ status: 'unauthenticated' });
  }, []);

  const hasRole = useCallback(
    (...roles: Role[]) => {
      if (state.status !== 'authenticated') return false;
      return roles.includes(state.user.role);
    },
    [state],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      user: state.status === 'authenticated' ? state.user : null,
      login,
      logout,
      hasRole,
    }),
    [state, login, logout, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth musi być używane wewnątrz <AuthProvider>.');
  return ctx;
}
